// src/components/Projects/ProjectPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Layers, Info, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import supabase from '../../supabase';
import ProjectPhases from './ProjectPhases';
import ProjectDetailPage from './ProjectDetailpage';
import './ProjectPage.css';

const ProjectPage = ({ project: initialProject, onBack }) => {
  const [activeTab, setActiveTab] = useState('phases');
  const [project, setProject] = useState(initialProject);
  const [loading, setLoading] = useState(false);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [phases, setPhases] = useState([]);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [phasesLoading, setPhasesLoading] = useState(true);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const tabsRef = useRef(null);

  useEffect(() => {
    if (initialProject?.id) {
      fetchProjectDetails();
      fetchPhases();
    }
  }, [initialProject?.id]);

  useEffect(() => {
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, [phases]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', initialProject.id)
        .single();
      if (error) throw error;

      if (data?.client_id) {
        const { data: clientData } = await supabase
          .from('clients')
          .select('client_name')
          .eq('id', data.client_id)
          .single();
        setProject({ ...data, client_name: clientData?.client_name || null });
      } else {
        setProject(data);
      }
    } catch (err) {
      console.error('Error fetching project details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPhases = async () => {
    try {
      setPhasesLoading(true);
      const { data, error } = await supabase
        .from('project_phases')
        .select('*')
        .eq('project_id', initialProject.id)
        .order('phase_order', { ascending: true });
      if (error) throw error;

      setPhases(data || []);
      if (data && data.length > 0 && !selectedPhaseId) {
        setSelectedPhaseId(data[0].id);
      }
    } catch (err) {
      console.error('Error fetching phases:', err);
    } finally {
      setPhasesLoading(false);
    }
  };

  const checkScrollButtons = () => {
    if (tabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  const scrollTabs = (direction) => {
    if (tabsRef.current) {
      const scrollAmount = 200;
      tabsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
      setTimeout(checkScrollButtons, 300);
    }
  };

  const handleBack = () => onBack();

  const handleProjectUpdate = () => {
    fetchProjectDetails();
    fetchPhases();
  };

  const handlePhaseSelect = (phaseId) => {
    setSelectedPhaseId(phaseId);
    setActiveTab('phase-detail');
  };

  const handleAddPhase = () => {
    setActiveTab('phases');
    setSelectedPhaseId(null);
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'Not Active': '#666',
      'Active': '#10b981',
      'Completed': '#3b82f6'
    };
    return statusMap[status] || '#6b7280';
  };

  const getPhaseStatusColor = (status) => {
    const colors = {
      'Not Active': '#666',
      'Active': '#2254c5',
      'Completed': '#000'
    };
    return colors[status] || '#666';
  };

  if (!project) {
    return (
      <div className="projectdetails-container">
        <div className="projectdetails-not-found">
          <h2>Project Not Found</h2>
          <p>No project data provided.</p>
          <button onClick={handleBack} className="projectdetails-back-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="projectdetails-container">
      <div className="projectdetails-wrapper">
        {/* Header (mirrors employee details structure) */}
        <div className="projectdetails-header">
          <div className="projectdetails-summary">
            <div className="projectdetails-basic">
              <div className="projectdetails-name-row">
                <h1 className="projectdetails-name">{project.name}</h1>
                <button
                  onClick={() => setShowDetailsPopup(true)}
                  className="projectdetails-details-btn"
                  title="View Details"
                >
                  <Info size={18} />
                  <span>Details</span>
                </button>
              </div>

              <div className="projectdetails-meta">
                {project.client_name && (
                  <span className="projectdetails-meta-item">{project.client_name}</span>
                )}
                {project.status && (
                  <span
                    className="projectdetails-status-badge"
                    style={{ backgroundColor: getStatusColor(project.status), color: 'white' }}
                  >
                    {project.status.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleBack}
            className="projectdetails-back-btn"
            title="Back to Projects"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>

        {/* Tabs (sticky, parallel to employee details) */}
        <div className="projectdetails-tabs">
          <div className="projectdetails-tab-list-wrap">
            {showLeftArrow && (
              <button
                className="projectdetails-tab-scroll-btn projectdetails-tab-scroll-left"
                onClick={() => scrollTabs('left')}
                aria-label="Scroll tabs left"
              >
                <ChevronLeft size={20} />
              </button>
            )}

            <div
              className="projectdetails-tab-list"
              ref={tabsRef}
              onScroll={checkScrollButtons}
            >
              <button
                className={`projectdetails-tab ${activeTab === 'phases' ? 'projectdetails-tab-active' : ''}`}
                onClick={() => { setActiveTab('phases'); setSelectedPhaseId(null); }}
              >
                <Layers size={18} />
                <span>All Phases</span>
              </button>

              {!phasesLoading && phases.map((phase) => (
                <button
                  key={phase.id}
                  className={`projectdetails-tab projectdetails-phase-tab ${
                    activeTab === 'phase-detail' && selectedPhaseId === phase.id ? 'projectdetails-tab-active' : ''
                  }`}
                  onClick={() => handlePhaseSelect(phase.id)}
                  title={phase.name}
                >
                  <span className="projectdetails-phase-order">{phase.phase_order}</span>
                  <span className="projectdetails-phase-name">{phase.name}</span>
                  <span
                    className="projectdetails-phase-status-dot"
                    style={{ backgroundColor: getPhaseStatusColor(phase.status) }}
                  />
                </button>
              ))}

              {!phasesLoading && phases.length === 0 && (
                <button
                  className="projectdetails-tab projectdetails-tab-add"
                  onClick={handleAddPhase}
                  title="Add New Phase"
                >
                  <Plus size={18} />
                </button>
              )}
            </div>

            {showRightArrow && (
              <button
                className="projectdetails-tab-scroll-btn projectdetails-tab-scroll-right"
                onClick={() => scrollTabs('right')}
                aria-label="Scroll tabs right"
              >
                <ChevronRight size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Tab content */}
        <div className="projectdetails-tab-content">
          {phasesLoading ? (
            <div className="projectdetails-loading">
              <div className="projectdetails-spinner"></div>
              <p>Loading...</p>
            </div>
          ) : activeTab === 'phases' ? (
            <ProjectPhases
              project={project}
              onProjectUpdate={handleProjectUpdate}
              mode="all"
            />
          ) : (
            <ProjectPhases
              project={project}
              onProjectUpdate={handleProjectUpdate}
              mode="detail"
              selectedPhaseId={selectedPhaseId}
            />
          )}
        </div>

        {/* Details popup */}
        {showDetailsPopup && (
          <div className="projectdetails-popup-overlay" onClick={() => setShowDetailsPopup(false)}>
            <div
              className={`projectdetails-popup-container ${showDetailsPopup ? 'projectdetails-popup-open' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <ProjectDetailPage
                project={project}
                onProjectUpdate={handleProjectUpdate}
                onClose={() => setShowDetailsPopup(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectPage;